import * as BrowserSocket from '@effect/platform-browser/BrowserSocket'
import * as PlatformSocket from 'effect/unstable/socket/Socket'
import { Cause, Effect, Fiber } from 'effect'
import {
  ConnectTransportError,
  type RealtimeTransport,
  type RealtimeTransportFactory
} from './types'

export const makePlatformTransport: RealtimeTransportFactory = (url, handlers) =>
  Effect.gen(function* () {
    const socket = yield* PlatformSocket.makeWebSocket(url, {
      closeCodeIsError: () => true
    })

    const writer = yield* socket.writer

    const runFiber = yield* socket
      .runRaw(handlers.onMessage, {
        onOpen: handlers.onOpen
      })
      .pipe(
        Effect.catchTag('SocketError', (error) => {
          if (error.reason._tag == 'SocketCloseError') {
            return handlers.onClose({
              code: error.reason.code,
              reason: error.reason.closeReason,
              wasClean: error.reason.code == 1000
            })
          }
          return handlers.onError(error)
        }),
        Effect.tapCause((cause) => {
          if (Cause.hasInterruptsOnly(cause)) {
            return Effect.void
          }
          return handlers.onDefect('transport reader terminated unexpectedly', Cause.squash(cause))
        }),
        Effect.forkScoped({ startImmediately: true })
      )

    const send: RealtimeTransport['send'] = (data) =>
      writer(data).pipe(
        Effect.mapError(
          (error) => new ConnectTransportError('failed to send realtime frame', error)
        )
      )

    const close: RealtimeTransport['close'] = (code = 1000, reason) =>
      writer(new PlatformSocket.CloseEvent(code, reason)).pipe(
        Effect.mapError(
          (error) => new ConnectTransportError('failed to close realtime socket', error)
        ),
        Effect.andThen(Fiber.interrupt(runFiber))
      )

    return {
      send,
      close
    } satisfies RealtimeTransport
  }).pipe(Effect.provide(BrowserSocket.layerWebSocketConstructor))
